import { createApi } from '@reduxjs/toolkit/dist/query/react';

import { baseQueryClient } from '@/api/api-client';

import { ApiTagsEnum } from '@/enum/apiTags.enum';

import {
  CLIENT_CODE,
  GAMEZONE_TILES_URL,
  TENANT_CODE,
} from '@/utils/constants';

import {
  GetGameZoneContestRes,
  GetGameZoneTemplateRes,
  GetUserProfileRes,
  GetUpcomingContestsRes,
  GetUserContestsRes,
  UpdateUserDataSubscriptionBody,
  UpdateUserProfileBody,
  LimitOfset,
} from '@/types/domain';

export const api = createApi({
  reducerPath: 'mainApi',
  tagTypes: Object.values(ApiTagsEnum),
  baseQuery: baseQueryClient,
  endpoints: (builder) => ({
    getGameZoneTemplate: builder.query<GetGameZoneTemplateRes, void>({
      query: () => ({
        url: `${GAMEZONE_TILES_URL}/clients/${CLIENT_CODE}/gamezone-template`,
        params: {
          tenant: TENANT_CODE,
        },
      }),
      providesTags: [ApiTagsEnum.GET_GAME_ZONE_TEMPLATE],
    }),
    getUpcomingContests: builder.query<GetUpcomingContestsRes, void>({
      query: () => ({
        url: '/contests',
        params: {
          limit: 4,
          offset: 0,
          tenant: TENANT_CODE,
        },
      }),
    }),
    getGameHistory: builder.query<GetUserContestsRes, void>({
      query: () => ({
        url: '/user/games-history',
        params: {
          limit: 4,
          offset: 0,
        },
      }),
    }),
    getUpcomingHistory: builder.query<GetUserContestsRes, void>({
      query: () => ({
        url: '/user/games-upcoming-history',
        params: {
          limit: 5,
          offset: 0,
        },
      }),
    }),
    getRecentHistory: builder.query<GetUserContestsRes, LimitOfset>({
      query: ({ limit = 5 }) => ({
        url: '/user/games-recent-history',
        params: {
          limit,
          offset: 0,
        },
      }),
    }),
    updateUserDataSubscription: builder.mutation<
      { success: boolean },
      UpdateUserDataSubscriptionBody
    >({
      query: (body) => ({
        url: '/user/notification-preferences',
        method: 'PUT',
        body,
      }),
    }),
    getUserProfile: builder.query<GetUserProfileRes, void>({
      query: () => ({
        url: '/user/me',
      }),
      providesTags: [ApiTagsEnum.GET_USER_PROFILE],
    }),
    updateUserProfile: builder.mutation<
      { success: boolean },
      UpdateUserProfileBody
    >({
      query: (body) => ({
        url: '/user',
        method: 'PUT',
        body,
      }),
      invalidatesTags: [ApiTagsEnum.GET_USER_PROFILE],
    }),
    getGameZoneContest: builder.query<GetGameZoneContestRes, { url: string }>({
      query: ({ url }) => ({ url }),
    }),
  }),
});

export const {
  useGetGameZoneTemplateQuery,
  useGetUpcomingContestsQuery,
  useGetUpcomingHistoryQuery,
  useGetRecentHistoryQuery,
  useUpdateUserDataSubscriptionMutation,
  useGetUserProfileQuery,
  useUpdateUserProfileMutation,
  useLazyGetGameZoneContestQuery,
} = api;
